// Accurate only while no analytics, embeds, external fonts or other third-party content are added: any
// such addition needs a review of this page and possibly a consent mechanism.
// Cookie notice: one strictly necessary cookie (eb_session), necessary browser storage (drafts,
// the consent choice) and optional storage with consent (display settings). No tracking. The
// cookie banner (ui/CookieBanner.tsx) collects the choice.
import { LegalLayout, LegalLink } from "./LegalLayout";

export function Cookies() {
  return (
    <LegalLayout slug="cookies" title="Cookie Notice">
      <h2 id="summary">In short</h2>
      <p>
        The Infinite uses <strong>one cookie</strong>, only to keep you signed in, and stores draft backups in your own
        browser. With your consent it also remembers your display settings on this device. Nothing is used for tracking,
        analytics or advertising, and no third party sets cookies through our pages. The cookie banner asks once; you
        can change your choice any time under “Cookie settings” at the bottom of every page.
      </p>

      <h2 id="cookie">The cookie</h2>
      <dl className="legal-dl">
        <dt>Name</dt>
        <dd>
          <code>eb_session</code>
        </dd>
        <dt>Purpose</dt>
        <dd>
          Keeps you signed in after you open a sign-in link. It holds a random token; our server stores only a hash of
          it and uses it to recognise your session.
        </dd>
        <dt>Set when</dt>
        <dd>You sign in. Visitors who only listen never get it.</dd>
        <dt>Lifetime</dt>
        <dd>
          30 days, extended while you use the site (sliding expiry). Removed when you sign out or delete your account.
        </dd>
        <dt>Properties</dt>
        <dd>
          First-party (our domain only), HTTP-only (page scripts can't read it), Secure (sent only over HTTPS),
          SameSite=Lax (not sent with cross-site requests that change data).
        </dd>
      </dl>
      <p>
        To protect you against forged requests, the app also keeps a separate security token (CSRF token) in the page's
        memory while it is open. It is not a cookie and is not stored in your browser.
      </p>

      <h2 id="storage">Storage in your browser</h2>
      <dl className="legal-dl">
        <dt>
          <code>eb.settings</code> (localStorage) — <em>preferences, only with your consent</em>
        </dt>
        <dd>
          Your chosen skin (Studio or Zoo) and whether calm motion is on, so the site looks the way you set it next
          time. Without consent the settings last only for your visit; withdrawing consent deletes the stored value.
          When you're signed in, your skin choice is also saved with your account.
        </dd>
        <dt>
          <code>eb.tour</code> (localStorage) — <em>preferences, only with your consent</em>
        </dt>
        <dd>
          Which guided tours you have taken or dismissed, so the welcome prompt isn't shown again. Without consent it
          lasts only for your visit; withdrawing consent deletes the stored value.
        </dd>
        <dt>
          <code>eb.consent</code> (localStorage) — <em>strictly necessary</em>
        </dt>
        <dd>Your cookie choice and when you made it, so we don't ask again. Not sent to our server.</dd>
        <dt>Statistics</dt>
        <dd>
          Not used. The category exists so that any future, anonymous statistics would only run with your consent.
        </dd>
        <dt>
          <code>endless-beat</code> (IndexedDB)
        </dt>
        <dd>
          Backups of loops you are writing in the editor (local drafts), so you don't lose work if the tab closes or the
          connection drops. They stay in your browser until you delete the draft or clear site data, and are sent to our
          server only when you save or submit a draft.
        </dd>
      </dl>

      <h2 id="legal">Which storage needs consent</h2>
      <p>
        Under § 25(2) no. 2 of the German Telecommunications Digital Services Data Protection Act (
        <span lang="de">TDDDG</span>) and Art. 5(3) of the EU ePrivacy Directive, storing information on your device
        needs no consent if it is strictly necessary to provide a service you explicitly asked for: staying signed in,
        keeping your unsent drafts and remembering your cookie choice. Remembering display settings across visits is
        optional, so we ask first (§ 25(1) TDDDG). How we process the data involved is explained in the{" "}
        <LegalLink to="privacy">Privacy Policy</LegalLink>.
      </p>

      <h2 id="control">Your control</h2>
      <p>
        You can view and delete cookies and site data at any time in your browser's settings. If you delete{" "}
        <code>eb_session</code>, you are signed out; if you delete local storage, your settings reset and local draft
        backups are lost (drafts saved on the server remain). Blocking cookies entirely means you can still listen, but
        not sign in.
      </p>
    </LegalLayout>
  );
}
